import {
  ButtonProps,
  Select,
  Input,
  SectionHeader,
  Text,
  Combobox,
  Spinner,
} from "@ttc3k/trekker";
import { Dispatch, SetStateAction } from "react";
import {
  EnumWidgetEventSettingsDisplayType,
  EnumWidgetType,
} from "gql/generated";
import { useTranslation } from "react-i18next";
import { useStartNewEventWidget } from "./useStartNewEventWidget";
import { Flex } from "styled-system/jsx";
import { ViewColumns3, ViewGrid } from "iconoir-react";
import { Collapsible } from "shared/components";
import { ButtonCard } from "shared/components/atoms/ButtonCard/ButtonCard";

type StartNewEventWidgetProps = {
  setOnCloseClick: Dispatch<SetStateAction<() => void>>;
  setCancelButtonProps: Dispatch<SetStateAction<ButtonProps>>;
  setNextButtonProps: Dispatch<SetStateAction<ButtonProps>>;
  onClose: () => void;
  onNextClick: (
    widgetType: EnumWidgetType,
    name: string,
    listId?: string,
    eventSettings?: {
      displayType: EnumWidgetEventSettingsDisplayType;
      lookAheadMonths: number;
      eventTags: string[];
    },
  ) => void;
};

export const StartNewEventWidget = ({
  setOnCloseClick,
  setCancelButtonProps,
  setNextButtonProps,
  onClose,
  onNextClick,
}: StartNewEventWidgetProps) => {
  const { t } = useTranslation();

  const {
    widgetDetails,
    lookAheadMonthsOptions,
    events,
    loadingEvents,
    errorEvents,
    allEventTags,
    filteredTags,
    handleFilterTags,
    handleComboboxOpenChange,
    setWidgetDetails,
  } = useStartNewEventWidget({
    onClose,
    onNextClick,
    setOnCloseClick,
    setCancelButtonProps,
    setNextButtonProps,
  });

  return (
    <Flex flexDir={"column"} gap={"250"}>
      <Flex flexDir={"column"} gap={"200"}>
        <Text visual={"bodySemiBold"}>
          {t("widgets:CREATE_NEW.EVENT_WIDGET.WIDGET_NAME.TITLE")}
        </Text>
        <Input
          onChange={(e) =>
            setWidgetDetails((prev) => ({ ...prev, name: e.target.value }))
          }
          hint={t("widgets:CREATE_NEW.EVENT_WIDGET.WIDGET_NAME.HINT")}
        />
      </Flex>
      <Flex flexDir={"column"} gap={"200"}>
        <Text visual={"bodySemiBold"}>
          {t("widgets:CREATE_NEW.EVENT_WIDGET.WIDGET_STYLE.TITLE")}
        </Text>
        <Flex flexDir="row" gap={"150"}>
          <ButtonCard
            title={t(
              "widgets:CREATE_NEW.EVENT_WIDGET.WIDGET_STYLE.FULL_CALENDAR.TITLE",
            )}
            description={t(
              "widgets:CREATE_NEW.EVENT_WIDGET.WIDGET_STYLE.FULL_CALENDAR.DESCRIPTION",
            )}
            Icon={ViewGrid}
            onClick={() => {
              setWidgetDetails((prev) => ({
                ...prev,
                eventSettings: {
                  ...prev.eventSettings,
                  displayType: EnumWidgetEventSettingsDisplayType.Grid,
                  lookAheadMonths: 0,
                },
              }));
            }}
            isSelected={
              widgetDetails.eventSettings.displayType ===
              EnumWidgetEventSettingsDisplayType.Grid
            }
          />
          <ButtonCard
            title={t(
              "widgets:CREATE_NEW.EVENT_WIDGET.WIDGET_STYLE.CURATED_EVENTS.TITLE",
            )}
            description={t(
              "widgets:CREATE_NEW.EVENT_WIDGET.WIDGET_STYLE.CURATED_EVENTS.DESCRIPTION",
            )}
            Icon={ViewColumns3}
            onClick={() => {
              setWidgetDetails((prev) => ({
                ...prev,
                eventSettings: {
                  ...prev.eventSettings,
                  displayType: EnumWidgetEventSettingsDisplayType.Carousel,
                  lookAheadMonths: 12,
                },
              }));
            }}
            isSelected={
              widgetDetails.eventSettings.displayType ===
              EnumWidgetEventSettingsDisplayType.Carousel
            }
          />
        </Flex>
      </Flex>

      <Collapsible
        open={
          widgetDetails.eventSettings.displayType ===
          EnumWidgetEventSettingsDisplayType.Carousel
        }
        style={{
          zIndex: 100,
        }}
      >
        <Flex flexDir={"column"} gap={"200"}>
          <SectionHeader
            text={t("widgets:CREATE_NEW.EVENT_WIDGET.LOOK_AHEAD.TITLE")}
            divider
          />
          <Text visual="bodyRegular" color="text.mid">
            {t("widgets:CREATE_NEW.EVENT_WIDGET.LOOK_AHEAD.DESCRIPTION")}
          </Text>
          <Select.Root
            items={lookAheadMonthsOptions}
            value={[widgetDetails.eventSettings.lookAheadMonths.toString()]}
            onValueChange={({ value }) => {
              setWidgetDetails((prev) => ({
                ...prev,
                eventSettings: {
                  ...prev.eventSettings,
                  lookAheadMonths: parseInt(value[0]),
                },
              }));
            }}
          >
            {lookAheadMonthsOptions.map((option) => (
              <Select.Item key={option.value} item={option} />
            ))}
          </Select.Root>
        </Flex>
      </Collapsible>

      {/* Selected filters */}
      <Flex flexDir={"column"} gap={"200"}>
        <SectionHeader
          text={t("widgets:CREATE_NEW.EVENT_WIDGET.FILTERS.TITLE")}
          divider
        />
        <Text visual="bodyRegular" color="text.mid">
          {t("widgets:CREATE_NEW.EVENT_WIDGET.FILTERS.DESCRIPTION")}
        </Text>
        <Combobox.Root
          items={allEventTags.flatMap((tag) => tag.tags)}
          onValueChange={({ value }) =>
            setWidgetDetails((prev) => ({
              ...prev,
              eventSettings: {
                ...prev.eventSettings,
                eventTags: value,
              },
            }))
          }
          value={widgetDetails.eventSettings.eventTags}
          onOpenChange={handleComboboxOpenChange}
          onInputValueChange={(d) => handleFilterTags(d.inputValue)}
          placeholder={`${t("widgets:CREATE_NEW.EVENT_WIDGET.FILTERS.PLACEHOLDER")}...`}
          multiple
          positioning={{
            placement: "top",
          }}
          tagVisualVariant={"accent.blue"}
        >
          {filteredTags.length > 0 &&
            filteredTags.map((tagGroup) => (
              <Combobox.ItemGroup key={tagGroup.group}>
                <Combobox.ItemGroupLabel>
                  {t(`events:APP.LOOKUPS.${tagGroup.group}`)}
                </Combobox.ItemGroupLabel>
                {tagGroup.tags.map((tag) => (
                  <Combobox.Item
                    paddingLeft={"300"}
                    key={tag.value}
                    item={tag}
                    label={tag.label}
                  />
                ))}
              </Combobox.ItemGroup>
            ))}
        </Combobox.Root>
      </Flex>

      {/* # of events found based on filters */}
      {loadingEvents ? (
        <Spinner color="text.accent.blue.mid" w="24px" h="24px" />
      ) : (
        <Text visual="bodySemiBold" color="text.accent.blue.mid">
          {t("widgets:CREATE_NEW.EVENT_WIDGET.EVENTS_FOUND", {
            count: errorEvents ? 0 : events?.length,
          })}
        </Text>
      )}
    </Flex>
  );
};
